import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { axios } from '../../context/auth'
import SongItem from '../../styled/song-item'
import { PrimaryButton } from '../../styled/button'
import { pageView, PAGES } from '../../utils/analytics-tracker'

const InvitationsContainer = styled.div`
  color: ${({ theme }) => theme.bodyText};
`

const InvitationsHeader = styled.div`
  margin-bottom: 2rem;
  
  h2 {
    color: ${({ theme }) => theme.bodyText};
  }
`

const Section = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    color: ${({ theme }) => theme.bodyText};
  }
`

const InvitationDetails = styled.div`
  color: ${({ theme }) => theme.bodyText};
  
  div {
    margin-bottom: 0.25rem;
  }
  
  strong {
    color: ${({ theme }) => theme.primary};
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const AcceptButton = styled(PrimaryButton)`
  background-color: ${({ theme }) => theme.secondary};
  border-color: ${({ theme }) => theme.secondary};
`

const DeclineButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.tertiary};
  color: ${({ theme }) => theme.primaryContrast};
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const CancelButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.secondaryBackground};
  color: ${({ theme }) => theme.bodyText};
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.tertiary};
`

const LoadingMessage = styled.div`
  color: ${({ theme }) => theme.bodyText};
`

const EmptyMessage = styled.p`
  color: ${({ theme }) => theme.bodyText};
  opacity: 0.7;
`

const InvitationsList = () => {
  const [receivedInvitations, setReceivedInvitations] = useState([])
  const [sentInvitations, setSentInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState({})

  useEffect(() => {
    loadInvitations()
    pageView(PAGES.INVITATIONS_LIST)
  }, [])

  const loadInvitations = async () => {
    try {
      setLoading(true)
      const [receivedResponse, sentResponse] = await Promise.all([
        axios.get('/api/invitations'),
        axios.get('/api/invitations/sent')
      ])
      setReceivedInvitations(receivedResponse.data.invitations || [])
      setSentInvitations(sentResponse.data.invitations || [])
      setError(null)
    } catch (err) {
      console.error('Error loading invitations:', err)
      setError('Failed to load invitations')
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async (invitationId) => {
    try {
      setActionLoading({ ...actionLoading, [invitationId]: 'accepting' })
      await axios.put(`/api/invitations/${invitationId}/accept`)
      await loadInvitations()
    } catch (err) {
      console.error('Error accepting invitation:', err)
      window.alert('Failed to accept invitation')
    } finally {
      setActionLoading({ ...actionLoading, [invitationId]: null })
    }
  }

  const handleDecline = async (invitationId) => {
    if (!window.confirm('Are you sure you want to decline this invitation?')) {
      return
    }
    try {
      setActionLoading({ ...actionLoading, [invitationId]: 'declining' })
      await axios.put(`/api/invitations/${invitationId}/decline`)
      await loadInvitations()
    } catch (err) {
      console.error('Error declining invitation:', err)
      window.alert('Failed to decline invitation')
    } finally {
      setActionLoading({ ...actionLoading, [invitationId]: null })
    }
  }

  const handleCancel = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return
    }
    try {
      setActionLoading({ ...actionLoading, [invitationId]: 'cancelling' })
      await axios.delete(`/api/invitations/${invitationId}`)
      await loadInvitations()
    } catch (err) {
      console.error('Error cancelling invitation:', err)
      window.alert('Failed to cancel invitation')
    } finally {
      setActionLoading({ ...actionLoading, [invitationId]: null })
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  if (loading) {
    return <LoadingMessage>Loading invitations...</LoadingMessage>
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>
  }

  return (
    <InvitationsContainer>
      <InvitationsHeader>
        <h2>My Invitations</h2>
      </InvitationsHeader>

      <Section>
        <h3>Received Invitations ({receivedInvitations.length})</h3>
        {receivedInvitations.length === 0
          ? (
            <EmptyMessage>No pending invitations</EmptyMessage>
            )
          : (
              receivedInvitations.map(invitation => (
                <SongItem key={invitation._id}>
                  <InvitationDetails>
                    <div><strong>{invitation.content?.title || 'Untitled'}</strong></div>
                    <div>Type: {invitation.contentType}</div>
                    <div>From: {invitation.inviterId?.email}</div>
                    <div>Permission: {invitation.permissionLevel}</div>
                    <div>Received: {formatDate(invitation.createdAt)}</div>
                  </InvitationDetails>
                  <ButtonGroup>
                    <AcceptButton
                      onClick={() => handleAccept(invitation._id)}
                      disabled={actionLoading[invitation._id]}
                    >
                      {actionLoading[invitation._id] === 'accepting'
                        ? 'Accepting...'
                        : 'Accept'}
                    </AcceptButton>
                    <DeclineButton
                      onClick={() => handleDecline(invitation._id)}
                      disabled={actionLoading[invitation._id]}
                    >
                      {actionLoading[invitation._id] === 'declining'
                        ? 'Declining...'
                        : 'Decline'}
                    </DeclineButton>
                  </ButtonGroup>
                </SongItem>
              ))
            )}
      </Section>

      <Section>
        <h3>Sent Invitations ({sentInvitations.length})</h3>
        {sentInvitations.length === 0
          ? (
            <EmptyMessage>No sent invitations</EmptyMessage>
            )
          : (
              sentInvitations.map(invitation => (
                <SongItem key={invitation._id}>
                  <InvitationDetails>
                    <div><strong>{invitation.content?.title || 'Untitled'}</strong></div>
                    <div>Type: {invitation.contentType}</div>
                    <div>To: {invitation.email}</div>
                    <div>Permission: {invitation.permissionLevel}</div>
                    <div>Status: {invitation.status}</div>
                    <div>Sent: {formatDate(invitation.createdAt)}</div>
                  </InvitationDetails>
                  {invitation.status === 'pending' && (
                    <ButtonGroup>
                      <CancelButton
                        onClick={() => handleCancel(invitation._id)}
                        disabled={actionLoading[invitation._id]}
                      >
                        {actionLoading[invitation._id] === 'cancelling'
                          ? 'Cancelling...'
                          : 'Cancel'}
                      </CancelButton>
                    </ButtonGroup>
                  )}
                </SongItem>
              ))
            )}
      </Section>
    </InvitationsContainer>
  )
}

export default InvitationsList
